import React from 'react';
import { atom } from 'jotai';
import type { AnswerT } from '../../../common/types/types';
import { loggerAtom } from '../../../modernization-common/logger/logger-atom';
import type { RemainingItemsDetailsT } from '../../../modernization-common/side-panel-m4/types';
import { REMAINING_ITEM_OBJECT, RemainingItemsErrorType } from '../../../modernization-common/side-panel-m4/types';
import type { QAMetadata } from '../../../qa-checks/type/common/types';
import type { ImageMetadataTypes, ImageQAEngine, QAAnnotationLabelAnswersInput } from '../../../qa-checks/type/qa-engine';
import { createRemainingItemFromQAError } from '../../../qa-checks/utils/create-remaining-items-from-qa-error';
import { RuleEngineActions } from '../components/rule-engine/rule-engine-actions';
import { annotationIdToLabelMapAtom, annotationMetadataAtom } from './annotation-atoms';
export const qaEngineAtom = atom<ImageQAEngine>();

// This atom should not be read outside the scope of rule engine. This is only used to sync task questionnaires values between two stores
export const taskQuestionnaireSyncAtom = atom<AnswerT>();
export const qaResultAtom = atom(get => {
  const ruleEngine = get(qaEngineAtom);
  if (!ruleEngine) {
    return {};
  }
  const metadata = get(annotationMetadataAtom);
  const taskQuestionnaire = get(taskQuestionnaireSyncAtom);
  const annotationLabelAnswers: QAAnnotationLabelAnswersInput[] = [...metadata.entries()].map(([id, m]) => ({
    id,
    labelTitle: m.labelId,
    answers: m.answers,
    dynamicAnswers: m.dynamicAnswers
  }));
  return ruleEngine.evaluateRuleOnTaskState({
    annotationLabelAnswers,
    taskQuestionnaire: taskQuestionnaire ?? {}
  });
});
type ImageQAReultInstanceMap = {
  [metadataId: string]: {
    ruleName: string;
    type: ImageMetadataTypes;
    errorMessage: string;
  }[];
};
export const instanceToRuleMap = atom(get => {
  const result = get(qaResultAtom);
  if (result instanceof Error) {
    return {};
  }
  const instanceMap: ImageQAReultInstanceMap = {};
  const ruleDetails = Object.entries(result).filter(([, ruleResult]) => !ruleResult.summarizedResult);
  for (const [ruleName, ruleDetail] of ruleDetails) {
    for (const instance of ruleDetail.instanceResult) {
      for (const metadata of instance.metadata) {
        if (!(metadata.id in instanceMap)) {
          instanceMap[metadata.id] = [];
        }
        instanceMap[metadata.id]?.push({
          ruleName,
          type: metadata.type,
          errorMessage: ruleDetail.errorMessage
        });
      }
    }
  }
  return instanceMap;
});
function getErrorMessage(errorMessage: string) {
  return `Rule failed with error - ${errorMessage}`;
}
export const qaEngineRemainingItemAtom = atom((get): RemainingItemsDetailsT[] => {
  const logger = get(loggerAtom);
  const idToLabelMap = get(annotationIdToLabelMapAtom);
  const result = get(qaResultAtom);
  if (result instanceof Error) {
    return createRemainingItemFromQAError(result);
  }
  const getLabelForRemainingItem = (metadata: QAMetadata<ImageMetadataTypes>) => {
    switch (metadata.type) {
      case 'TASK_QUESTIONNAIRE':
        {
          return 'Task Classification';
        }
      case 'ANNOTATION':
        {
          const annotationLabel = idToLabelMap.get(metadata.id);
          if (!annotationLabel) {
            logger?.error('Invalid annotation id passed in metadata');
            return;
          }
          return `${annotationLabel.title} (${metadata.id})`;
        }
    }
  };
  const allRemainingItems: RemainingItemsDetailsT[] = [];
  const ruleEntries = Object.entries(result).filter(([, ruleResult]) => !ruleResult.summarizedResult);
  for (const [ruleName, ruleResult] of ruleEntries) {
    for (const instanceResult of ruleResult.instanceResult) {
      if (instanceResult.result) {
        continue;
      }
      const firstMetadata = instanceResult.metadata[0];
      if (!firstMetadata) {
        logger?.error('Metadata should not be empty');
        continue;
      }
      // Unique identifier for all failing instances for a rule
      const mergeMetadataIds = instanceResult.metadata.map(({
        id
      }) => id).join('-');
      allRemainingItems.push({
        id: `${ruleName}-${mergeMetadataIds}`,
        label: getLabelForRemainingItem(firstMetadata) ?? '',
        type: 'ERROR',
        objectType: firstMetadata.type === 'ANNOTATION' ? REMAINING_ITEM_OBJECT.ANNOTATION : REMAINING_ITEM_OBJECT.TASK,
        errorType: RemainingItemsErrorType.RULE_ENGINE,
        message: getErrorMessage(ruleResult.errorMessage),
        subLabelOverride: ruleName,
        customActions: <RuleEngineActions metadata={instanceResult.metadata} ruleResult={ruleResult} />
      });
    }
  }
  return allRemainingItems;
});
export type RuleEngineAtomHydratorList = readonly [typeof qaEngineAtom, ImageQAEngine | undefined];